import { FetchResult, gql } from '@apollo/client/core';
import { AntipatternType, AntipatternTypeV2, CopilotNotificationStatus } from '@coach-bot/data-access/shared/enums';
import { AntipatternData } from '@coach-bot/data-access/shared/interfaces';
import { AntipatternConfiguration } from '@coach-bot/data-access/shared/models';
import { GraphQLClientService } from '@coach-bot/data-access/shared/services/graphql-client.service';
import { isEligibleForPopUp } from '@coach-bot/features/antipattern/utils';
import { AppAction, ContentScriptAction } from '@shared/extension/enums';
import { ServiceWorkerCommunicationService } from '@shared/extension/services/service-worker-communication.service';
import { dispatchAppDocumentEvent, unloadOnOrphaned } from '@shared/extension/utils/events';
import { withChromeRuntime } from '@shared/extension/utils/runtime';

interface CopilotNotificationData {
  onCreateCopilotNotification: {
    id: string;
    message: string;
    notification_type: string;
    status: CopilotNotificationStatus;
    type: AntipatternType;
  }
}

const ALLOWED_DOMAINS = ['trilogy.com', 'devfactory.com'];
const ANTIPATTERN_POPUP_V2_CONFIGURATION_VERSION = '2.1';
const EVENT_HANDLER_DEBOUNCE_TIME_MS = 500;
const SKIP_EXPLANATION_READ_THEORY_POPUP_ID = 'skip-explanation-read-theory';
const TEST_USER_SUBSTRING = 'test';
const UI_GENERATED_NOTIFICATION_TYPE = 'UI Generated';

// Subscribe to a GraphQL subscription with a dynamic studentEmail value
const SUBSCRIPTION = gql`
  subscription NotificationListener($studentEmail: AWSEmail!) {
    onCreateCopilotNotification(studentEmail: $studentEmail) {
      createdOn
      id
      message
      status
      studentEmail
      type
    }
  }
`;

let currentSubscription: ZenObservable.Subscription | null = null;
const graphQLClient = GraphQLClientService.getInstance();
const serviceWorkerCommunicationService = new ServiceWorkerCommunicationService();
let studentEmail: string | null = null;

function debounce<T extends any[], C>(this: C, fn: (this: C, ...args: T) => void, delay: number): (...args: T) => void {
  let timer: ReturnType<typeof setTimeout> | null;

  return function (this: C, ...args: T) {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      timer = null;
      fn.apply(this, args);
    }, delay);
  }.bind(this);
}

function subscribeToNotifications(email: string) {
  if (currentSubscription) {
    currentSubscription.unsubscribe();
  }
  currentSubscription = graphQLClient
    .subscribe(SUBSCRIPTION, { studentEmail: email })
    .filter(
      (notification: FetchResult<CopilotNotificationData>) =>
        notification.data?.onCreateCopilotNotification.notification_type !== UI_GENERATED_NOTIFICATION_TYPE
    )
    .subscribe({
      next(data: any) {
        debouncedEventHandler(data, email);
      },
      error(error) {
        console.error(error, 'Error');
      },
    });
}

const debouncedEventHandler = debounce((notification: FetchResult<CopilotNotificationData>, email: string) => {
  withChromeRuntime(() => {
    if (!notification.data) {
      return;
    }
    const { id, message, type, status } = notification.data.onCreateCopilotNotification;
    // Get the email domain of the current user
    const emailDomain = email.split('@')[1];
    const isTestUser = email.startsWith(TEST_USER_SUBSTRING) || emailDomain.startsWith(TEST_USER_SUBSTRING);

    const isActiveOrTesting =
      (status === CopilotNotificationStatus.Testing && (ALLOWED_DOMAINS.includes(emailDomain) || isTestUser)) ||
      status === CopilotNotificationStatus.Active;

    if (isActiveOrTesting && isEligibleForPopUp()) {
      const configuration: AntipatternConfiguration = JSON.parse(message);
      showAntipatternPopup(id, configuration, type);
    }
  });
}, EVENT_HANDLER_DEBOUNCE_TIME_MS);

async function showAntipatternPopup(
  notificationId: string,
  configuration: AntipatternConfiguration,
  type: AntipatternType | AntipatternTypeV2
): Promise<void> {
  await waitForElementToHide(SKIP_EXPLANATION_READ_THEORY_POPUP_ID);
  if (configuration.version === ANTIPATTERN_POPUP_V2_CONFIGURATION_VERSION) {
    dispatchAppDocumentEvent(
      new CustomEvent(AppAction.ShowAntipatternPopupV2, {
        detail: { notificationId, configuration, type: configuration.type } as AntipatternData,
      })
    );
  } else {
    dispatchAppDocumentEvent(
      new CustomEvent(AppAction.ShowAntipatternPopup, {
        detail: { notificationId, configuration, type } as AntipatternData,
      })
    );
  }
}

async function waitForElementToHide(elementId: string): Promise<void> {
  return new Promise((resolve) => {
    const element: HTMLElement = document.getElementById(elementId);
    if (!element || element.style.display === 'none') {
      resolve();
      return;
    }
    const observer = new MutationObserver((mutations, obs) => {
      if (!document.body.contains(element) || element.style.display === 'none') {
        obs.disconnect();
        resolve();
      }
    });
    observer.observe(document.body, { attributes: true, childList: true, subtree: true });
  });
}

(async () => {
  studentEmail = await serviceWorkerCommunicationService.getChromeProfileEmail();
  subscribeToNotifications(studentEmail);

  chrome.runtime.onMessage.addListener(function(request, sender, sendResponse) {
    if (request.action === ContentScriptAction.ExtensionAuthStatusChanged) {
      if (studentEmail !== request.detail.email) {
        studentEmail = request.detail.email;
        if (studentEmail) {
          subscribeToNotifications(studentEmail);
        }
      }
    }
  });

  unloadOnOrphaned('install-apollo-client-scripts', () => {
    graphQLClient.stop();
  });
})().then(() => console.log('Initialized apollo-client-script'));
